import axiosInstance from './axiosInstance'
import { URL as API_URL } from '../constants/url'
import { isIosDevice, isRunningAsStandalonePwa } from './pwa'

export type PushPermissionState =
  | NotificationPermission
  | 'prompt'
  | 'unsupported'

export type WebPushSubscriptionPayload = {
  endpoint: string
  keys: {
    p256dh: string
    auth: string
  }
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from(raw, (char) => char.charCodeAt(0))
}

function parseVapidPublicKey(payload: unknown): string | null {
  if (typeof payload === 'string' && payload) {
    return payload
  }

  if (!payload || typeof payload !== 'object') {
    return null
  }

  const data = payload as Record<string, unknown>
  const nested =
    data.data && typeof data.data === 'object'
      ? (data.data as Record<string, unknown>)
      : null

  const candidates = [
    data.public_key,
    data.publicKey,
    data.vapid_public_key,
    nested?.public_key,
    nested?.publicKey,
  ]

  const key = candidates.find((item) => typeof item === 'string' && item)
  return typeof key === 'string' ? key : null
}

export function isWebPushSupported() {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function isIosWithoutInstalledPwa() {
  return isIosDevice() && !isRunningAsStandalonePwa()
}

export async function getPushPermissionState(): Promise<PushPermissionState> {
  if (!isWebPushSupported()) {
    return 'unsupported'
  }

  try {
    const registration = await navigator.serviceWorker.ready
    const state = await registration.pushManager.permissionState({
      userVisibleOnly: true,
    })
    if (state === 'granted' || state === 'denied' || state === 'prompt') {
      return state
    }
  } catch {
    // fallback to Notification.permission
  }

  return Notification.permission
}

export async function getCurrentPushSubscription() {
  if (!isWebPushSupported()) {
    return null
  }

  const registration = await navigator.serviceWorker.ready
  return registration.pushManager.getSubscription()
}

export async function fetchVapidPublicKey() {
  const fromEnv = process.env.REACT_APP_VAPID_PUBLIC_KEY
  if (fromEnv) {
    return fromEnv
  }

  const response = await axiosInstance.get(`/${API_URL.pushVapidPublicKey}`)
  const key = parseVapidPublicKey(response.data)

  if (!key) {
    throw new Error('VAPID public key is empty')
  }

  return key
}

async function sendSubscriptionToServer(
  subscription: PushSubscription
) {
  const json = subscription.toJSON()
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('В подписке нет обязательных полей')
  }

  const body: WebPushSubscriptionPayload = {
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    },
  }

  await axiosInstance.post(`/${API_URL.pushSubscribe}`, body)
}

export async function subscribeToWebPush() {
  if (!isWebPushSupported()) {
    throw new Error('Push в этом браузере не поддерживается')
  }

  if (isIosWithoutInstalledPwa()) {
    throw new Error(
      'На iPhone уведомления работают только в установленном приложении'
    )
  }

  const vapidPublicKey = await fetchVapidPublicKey()
  const registration = await navigator.serviceWorker.ready
  let subscription = await registration.pushManager.getSubscription()

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    })
  }

  await sendSubscriptionToServer(subscription)
  return subscription
}

export async function unsubscribeFromWebPush() {
  const subscription = await getCurrentPushSubscription()
  if (!subscription) {
    return
  }

  try {
    await axiosInstance.post(`/${API_URL.pushUnsubscribe}`, {
      endpoint: subscription.endpoint,
    })
  } catch {
    // сервер мог ещё не быть готов
  }

  await subscription.unsubscribe()
}

export async function unsubscribePushOnLogout() {
  const subscription = await getCurrentPushSubscription()
  if (!subscription) {
    return
  }

  try {
    await axiosInstance.post(`/${API_URL.pushUnsubscribe}`, {
      endpoint: subscription.endpoint,
    })
  } catch {
    // выход не блокируем
  }
}

export async function syncWebPushSubscription() {
  if (!isWebPushSupported() || isIosWithoutInstalledPwa()) {
    return
  }

  const permission = await getPushPermissionState()
  if (permission !== 'granted') {
    return
  }

  const existing = await getCurrentPushSubscription()
  if (existing) {
    try {
      await sendSubscriptionToServer(existing)
    } catch {
      // бэкенд ещё не принял подписку
    }
    return
  }

  try {
    await subscribeToWebPush()
  } catch {
    // нет VAPID или API
  }
}

export async function showLocalTestNotification() {
  if (!('Notification' in window)) {
    throw new Error('Notifications API недоступен')
  }

  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      throw new Error('Разрешение на уведомления не выдано')
    }
  }

  const registration = await navigator.serviceWorker.ready
  const controller = navigator.serviceWorker.controller

  if (controller) {
    controller.postMessage({
      type: 'TEST_NOTIFICATION',
      title: 'Alex Logistic',
      body: 'Тестовое уведомление. Если вы это видите — SW и баннер работают.',
      url: '/profile',
    })
    return
  }

  await registration.showNotification('Alex Logistic', {
    body: 'Тестовое уведомление. Если вы это видите — SW и баннер работают.',
    icon: '/icons/icon-192.png',
    badge: '/icons/icon-192.png',
    data: { url: '/profile' },
  })
}

export function listenWebPushClientEvents() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return () => undefined
  }

  const onMessage = (event: MessageEvent) => {
    const type = event.data?.type
    if (type === 'ALEX_PUSH_NAVIGATE' && typeof event.data.url === 'string') {
      try {
        const url = new URL(event.data.url, window.location.origin)
        if (url.origin === window.location.origin) {
          window.location.assign(`${url.pathname}${url.search}${url.hash}`)
        }
      } catch {
        // ignore malformed url
      }
    }

    if (type === 'ALEX_PUSH_RESUBSCRIBE') {
      syncWebPushSubscription()
    }
  }

  navigator.serviceWorker.addEventListener('message', onMessage)

  return () => {
    navigator.serviceWorker.removeEventListener('message', onMessage)
  }
}
